import { useState, useCallback, useMemo } from 'react';
import { DateFormats } from 'infrastructure/enums';
import { getPresetByKey } from 'components/molecules/oneplaform/timeframe-filter/constants';

import type { PresetKeyEnum } from 'infrastructure/enums';
import type { Dayjs } from 'dayjs';
import type { ITimeframeFilterProps } from 'components/molecules/oneplaform/timeframe-filter/types';

export interface TDateRange {
  start: string;
  end: string;
}

interface TUseTimeframePickerReturn {
  selectedRange: [Dayjs | null, Dayjs | null];
  onRangeChange: (
    dates: null | (Dayjs | null)[],
    dateStrings: string[],
  ) => void;
  clearSelection: () => void;
  hasSelection: boolean;
  dateRangeFormatted: string;
  dateRangeObject: TDateRange | null;
  timeframeFilterProps: ITimeframeFilterProps;
}

export const useTimeframePicker = (
  onSelectionChange?: (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: string[],
  ) => void,
  defaultPresetKey?: PresetKeyEnum | string,
): TUseTimeframePickerReturn => {
  const defaultRange = defaultPresetKey
    ? getPresetByKey(defaultPresetKey)
    : null;

  const [selectedRange, setSelectedRange] = useState<
    [Dayjs | null, Dayjs | null]
  >(defaultRange || [null, null]);

  const hasSelection = useMemo(
    () => selectedRange[0] !== null && selectedRange[1] !== null,
    [selectedRange],
  );

  const dateRangeFormatted = useMemo(() => {
    if (!hasSelection) {
      return 'No date range selected';
    }
    return `${selectedRange[0]?.format(
      DateFormats['MM/DD/YYYY'],
    )} - ${selectedRange[1]?.format(DateFormats['MM/DD/YYYY'])}`;
  }, [hasSelection, selectedRange]);

  const dateRangeObject = useMemo<TDateRange | null>(() => {
    if (!hasSelection) {
      return null;
    }
    return {
      start: selectedRange[0]?.format(DateFormats['YYYY-MM-DD']) || '',
      end: selectedRange[1]?.format(DateFormats['YYYY-MM-DD']) || '',
    };
  }, [hasSelection, selectedRange]);

  const onRangeChange = useCallback(
    (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
      if (dates) {
        const range: [Dayjs | null, Dayjs | null] = [dates[0], dates[1]];
        setSelectedRange(range);

        if (onSelectionChange) {
          onSelectionChange(range, dateStrings);
        }
      } else {
        setSelectedRange([null, null]);
        if (onSelectionChange) {
          onSelectionChange([null, null], ['', '']);
        }
      }
    },
    [onSelectionChange],
  );

  const clearSelection = useCallback(() => {
    const resetRange = defaultRange || [null, null];
    setSelectedRange(resetRange);
    if (onSelectionChange) {
      if (defaultRange) {
        onSelectionChange(resetRange, [
          resetRange[0]?.format(DateFormats['MM/DD/YYYY']) || '',
          resetRange[1]?.format(DateFormats['MM/DD/YYYY']) || '',
        ]);
      } else {
        onSelectionChange([null, null], ['', '']);
      }
    }
  }, [onSelectionChange, defaultRange]);

  const timeframeFilterProps: ITimeframeFilterProps = useMemo(
    () => ({
      selectedRange,
      onRangeChange,
      clearSelection,
    }),
    [selectedRange, onRangeChange, clearSelection],
  );

  return {
    selectedRange,
    onRangeChange,
    clearSelection,
    hasSelection,
    dateRangeFormatted,
    dateRangeObject,
    timeframeFilterProps,
  };
};
